// File classification + optimal-selection helpers shared by the per-CIN files
// popup and the multi-CIN batch extraction flow. All paths here are tree paths
// relative to the company folder; callers prefix the CIN to build S3 keys.

import { FileTree } from "../FileExplorer/FileExplorer";
import {
  ExtractedDate,
  extractFileDate,
  toISODate,
} from "../../utils/extractFileDate";

export type FileTypeKey =
  | "ALL"
  | "PAS-3"
  | "MGT-7"
  | "MGT-14"
  | "AOC-4"
  | "MOA"
  | "SH-7"
  | "SH-8"
  | "SH-9"
  | "SH-11";

// Human-readable name for each form, used for tab tooltips so the abbreviation
// is self-explanatory — these are MCA (Ministry of Corporate Affairs) e-forms.
export const FORM_TYPE_LABELS: Partial<Record<FileTypeKey, string>> = {
  "AOC-4": "Financial Statements filed with the Registrar of Companies",
  MOA: "Memorandum of Association (also filed as e-MoA / Form INC-33)",
  "SH-7": "Notice to Registrar of any alteration of share capital",
  "SH-8": "Letter of Offer for buy-back of securities",
  "SH-9": "Declaration of Solvency (buy-back)",
  "SH-11": "Return in respect of buy-back of securities",
};

function normalizeName(name: string): string {
  return name
    .toUpperCase()
    .normalize("NFKD")
    .replace(/[^A-Z0-9]/g, "");
}

const FINANCIAL_TOKENS = ["AOC4", "AOC-4", "AOC 4", "AOC4XBRL"];
// Some companies upload the MOA spelled out in full instead of the "MOA"
// abbreviation, in which case the normalized filename reads
// "...MEMORANDUMOFASSOCIATION..." — "MOA" is not a contiguous substring of
// that (the letters land as "...UMOF..."), so it needs its own token.
const MOA_TOKENS = [
  "MOA",
  "INC33",
  "INC-33",
  "EMOA",
  "MEMORANDUMOFASSOCIATION",
];

export function classifyFile(file: string): FileTypeKey | null {
  const base = file.split("/").pop() ?? file;
  const normalized = normalizeName(base);
  if (base.toLowerCase().endsWith(".pdf")) {
    if (normalized.includes("PAS3")) return "PAS-3";
    if (normalized.includes("MGT14")) return "MGT-14";
    if (normalized.includes("MGT7")) return "MGT-7";
    if (normalized.includes("AOC4")) return "AOC-4";
    if (normalized.includes("SH11")) return "SH-11";
    if (normalized.includes("SH7")) return "SH-7";
    if (normalized.includes("SH8")) return "SH-8";
    if (normalized.includes("SH9")) return "SH-9";
    if (MOA_TOKENS.some((t) => normalized.includes(normalizeName(t))))
      return "MOA";
  }
  // AOC-4 is the financial filing — fold financial-keyword matches into AOC-4
  if (FINANCIAL_TOKENS.some((t) => normalized.includes(t))) return "AOC-4";
  return null;
}

// fetchCompanyFiles returns a tree where every level's keys are full paths
// from the root, so we use the key itself as the path instead of joining
// with the parent prefix (joining produced doubled prefixes like
// ".../Other eForm Documents/.../Other eForm Documents/...pdf").
export function flattenFileTree(tree: FileTree | undefined): string[] {
  if (!tree) return [];
  return Object.entries(tree).flatMap(([key, value]) => {
    if (value === "file") return [key];
    if (typeof value === "object") return flattenFileTree(value);
    return [];
  });
}

// MCA filenames are prefixed with a running sequence number, e.g.
// "83_Form PAS_3_14_03_2024.pdf". When a company re-uploads a corrected form
// under the same filing date, the corrected copy gets a higher number — so the
// highest leading number is the latest/valid copy for that date.
export function extractFormSequence(path: string): number | null {
  const base = path.split("/").pop() ?? path;
  const m = base.match(/^\s*(\d+)\s*[_\-.\s]/);
  return m ? Number(m[1]) : null;
}

type DupCandidate = {
  path: string;
  extracted: ExtractedDate | null;
  seq: number | null;
};

// True when `a` should win over `b` as the canonical filing for a date: prefer
// the higher sequence number; a numbered file beats an unnumbered one;
// otherwise fall back to filename order so the choice is deterministic.
function preferCandidate(a: DupCandidate, b: DupCandidate): boolean {
  if (a.seq !== null && b.seq !== null) return a.seq > b.seq;
  if (a.seq !== null) return true;
  if (b.seq !== null) return false;
  return a.path.localeCompare(b.path) > 0;
}

// Collapse a set of filings (PAS-3 or AOC-4) to one per filing date. When
// several share a date — a company re-uploaded a corrected copy — keep the one
// with the highest leading sequence number and record the rest as superseded so
// the UI can flag them. Files with no detectable date can't be deduped, so each
// gets its own key and is kept rather than silently dropped.
export function dedupeByFilingDate(
  paths: string[],
  fileDates: Map<string, ExtractedDate | null>,
): { keptPaths: string[]; superseded: Map<string, string> } {
  const candidates: DupCandidate[] = paths.map((path) => ({
    path,
    extracted: fileDates.get(path) ?? null,
    seq: extractFormSequence(path),
  }));

  const byDate = new Map<string, DupCandidate[]>();
  candidates.forEach((c) => {
    const key = c.extracted
      ? `date:${c.extracted.date.getTime()}`
      : `nodate:${c.path}`;
    const group = byDate.get(key);
    if (group) group.push(c);
    else byDate.set(key, [c]);
  });

  const keptPaths: string[] = [];
  // path -> the chosen sibling that supersedes it (for the "skipped" badge).
  const superseded = new Map<string, string>();
  byDate.forEach((group) => {
    const best = group.reduce((a, b) => (preferCandidate(b, a) ? b : a));
    keptPaths.push(best.path);
    group.forEach((c) => {
      if (c.path !== best.path) superseded.set(c.path, best.path);
    });
  });

  return { keptPaths, superseded };
}

export interface SelectedFileRef {
  path: string;
  // Filing date parsed from the filename, as YYYY-MM-DD (UTC), or null.
  date: string | null;
}

// The optimal extraction inputs derived from one company's file tree: the
// latest MGT-7 (annual share-register snapshot), every PAS-3 allotment and
// every AOC-4 financial filing, each deduplicated to one filing per date.
export interface CompanyAutoSelection {
  mgt7: SelectedFileRef | null;
  pas3: SelectedFileRef[];
  aoc4: SelectedFileRef[];
  // Duplicate-date copies that were skipped in favour of a corrected sibling.
  skippedDuplicates: number;
  warnings: string[];
}

function byDateDesc(
  fileDates: Map<string, ExtractedDate | null>,
): (a: string, b: string) => number {
  return (a, b) => {
    const ta = fileDates.get(a)?.date.getTime();
    const tb = fileDates.get(b)?.date.getTime();
    if (ta === undefined && tb === undefined) return a.localeCompare(b);
    if (ta === undefined) return 1;
    if (tb === undefined) return -1;
    return tb - ta;
  };
}

export function computeAutoSelection(
  tree: FileTree | undefined,
): CompanyAutoSelection {
  const files = flattenFileTree(tree);
  const fileDates = new Map<string, ExtractedDate | null>();
  for (const f of files) fileDates.set(f, extractFileDate(f));

  const toRef = (path: string): SelectedFileRef => ({
    path,
    date: toISODate(fileDates.get(path) ?? null),
  });

  const warnings: string[] = [];

  // Latest MGT-7 by filing date; when none of the MGT-7s carries a detectable
  // date, fall back to the sequence-number/filename preference so a batch run
  // still gets a deterministic anchor instead of silently skipping the company.
  const mgt7Files = files.filter((f) => classifyFile(f) === "MGT-7");
  let mgt7Path: string | null = null;
  let mgt7Ts = -Infinity;
  mgt7Files.forEach((f) => {
    const ts = fileDates.get(f)?.date.getTime();
    if (ts !== undefined && ts > mgt7Ts) {
      mgt7Ts = ts;
      mgt7Path = f;
    }
  });
  if (!mgt7Path && mgt7Files.length > 0) {
    const candidates: DupCandidate[] = mgt7Files.map((path) => ({
      path,
      extracted: null,
      seq: extractFormSequence(path),
    }));
    mgt7Path = candidates.reduce((a, b) =>
      preferCandidate(b, a) ? b : a,
    ).path;
    warnings.push("MGT-7 has no detectable date — picked by sequence number");
  }

  const pas3Files = files.filter((f) => classifyFile(f) === "PAS-3");
  const pas3Dedup = dedupeByFilingDate(pas3Files, fileDates);
  const aoc4Files = files.filter((f) => classifyFile(f) === "AOC-4");
  const aoc4Dedup = dedupeByFilingDate(aoc4Files, fileDates);

  const skippedDuplicates =
    pas3Dedup.superseded.size + aoc4Dedup.superseded.size;
  if (skippedDuplicates > 0) {
    warnings.push(
      `${skippedDuplicates} duplicate-date cop${
        skippedDuplicates === 1 ? "y" : "ies"
      } skipped`,
    );
  }

  return {
    mgt7: mgt7Path ? toRef(mgt7Path) : null,
    pas3: pas3Dedup.keptPaths.sort(byDateDesc(fileDates)).map(toRef),
    aoc4: aoc4Dedup.keptPaths.sort(byDateDesc(fileDates)).map(toRef),
    skippedDuplicates,
    warnings,
  };
}
